"use client";

import type { AppSidebarSection } from "@shared/components/app-sidebar";
import { AppSidebarShell } from "@shared/components/app-sidebar";
import { Sidebar, SidebarFooter } from "@shared/components/ui/sidebar";
import { IconMessageCircle } from "@tabler/icons-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type * as React from "react";
import { Suspense } from "react";
import { NavSidebarUtility } from "@/components/nav-sidebar-utility";
import { NavUser } from "@/components/nav-user";
import { useAuth } from "@/lib/auth";
import {
  appSidebarNavAccount,
  appSidebarNavDeveloper,
  appSidebarNavMain,
  appSidebarNavSettings,
} from "@/lib/navigation/app-sidebar-nav";
import { orgRoleCanUseChat } from "@/lib/org/org-constants";

const AppSidebarContent = ({ ...props }: React.ComponentProps<typeof Sidebar>) => {
  const pathname = usePathname();
  const { user, loading, chatSettings, orgKind, orgRole } = useAuth();

  const navUser = loading
    ? { name: "Loading…" }
    : user
      ? { name: user.name, email: user.email, avatar: user.avatarUrl }
      : { name: "Sign in" };

  const chatItems =
    chatSettings?.globally_enabled !== false && orgRoleCanUseChat(orgRole)
      ? [
          {
            title: "Chat",
            url: "/chat/",
            icon: <IconMessageCircle className="ra-sidebar__menu-icon" aria-hidden />,
            isExternal: true,
            onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              window.open("/chat/", "radium-chat");
            },
          },
        ]
      : [];

  const settingsItems = appSidebarNavSettings
    .filter((item) => item.url !== "/manage/org" || orgKind === "team")
    .map((item) => ({
      title: item.title,
      url: item.url,
      icon: <item.icon className="ra-sidebar__menu-icon" aria-hidden />,
    }));

  const sections: AppSidebarSection[] = [
    {
      label: "",
      items: [
        ...appSidebarNavMain.map((item) => ({
          title: item.title,
          url: item.url,
          icon: <item.icon className="ra-sidebar__menu-icon" aria-hidden />,
        })),
        ...appSidebarNavDeveloper.map((item) => ({
          title: item.title,
          url: item.url,
          icon: <item.icon className="ra-sidebar__menu-icon" aria-hidden />,
        })),
        ...appSidebarNavAccount.map((item) => ({
          title: item.title,
          url: item.url,
          icon: <item.icon className="ra-sidebar__menu-icon" aria-hidden />,
        })),
        ...chatItems,
        ...settingsItems,
      ],
    },
  ];

  return (
    <Sidebar collapsible="icon" {...props}>
      <AppSidebarShell
        activeUrl={pathname}
        sections={sections}
        hideCta
        renderLink={({ href, className, onClick, children, ...rest }) => (
          <Link href={href} className={className} onClick={onClick} {...rest}>
            {children}
          </Link>
        )}
        renderImage={({ src, alt, className, "aria-hidden": ariaHidden, width, height }) => (
          <Image
            src={src}
            alt={alt}
            className={className}
            aria-hidden={ariaHidden}
            width={width}
            height={height}
            priority
          />
        )}
        footerSlot={null}
      />
      {/* Footer lives outside AppSidebarShell so NavUser can use useSidebar() context */}
      <SidebarFooter className="gap-0 border-t border-border p-0">
        <NavSidebarUtility />
        <NavUser user={navUser} isAuthenticated={!!user} />
      </SidebarFooter>
    </Sidebar>
  );
};

export const AppSidebar = ({ ...props }: React.ComponentProps<typeof Sidebar>) => {
  return (
    <Suspense fallback={null}>
      <AppSidebarContent {...props} />
    </Suspense>
  );
};
